import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { NgFor, NgIf } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatChipGrid, MatChipInput, MatChipRemove, MatChipRow } from '@angular/material/chips';
import { MatOption } from '@angular/material/core';
import { MatDateRangeInput, MatDateRangePicker, MatEndDate, MatStartDate } from '@angular/material/datepicker';
import { MatError, MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatSelect } from '@angular/material/select';
import { ChecklistItem, SchadenUebersicht } from '@gsm/entities';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import moment from 'moment';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { checkForSpecialCharacters, replaceSpecialCharacters } from '../../../../utils/string.util';

// @ts-ignore
@UntilDestroy()
@Component({
  selector:    'gsm-overview-filter-schaden',
  templateUrl: './overview-filter.component.html',
  styleUrls:   ['./overview-filter.component.scss'],
  standalone:  true,
  imports: [
    MatIconButton,
    MatMenuTrigger,
    MatIcon,
    MatFormField,
    MatChipGrid,
    NgFor,
    MatChipRow,
    MatChipRemove,
    FormsModule,
    MatChipInput,
    ReactiveFormsModule,
    MatMenu,
    NgIf,
    MatMenuItem,
    MatDateRangeInput,
    MatStartDate,
    MatEndDate,
    MatDateRangePicker,
    MatError,
    MatSelect,
    MatOption,
    MatButton,
    MatInput
  ]
})
export class OverviewFilterComponent implements OnInit,
                                                OnChanges {
  schadenNr: string;
  polizzenNr: string;
  meldedatum: string;
  meldedatumStart: string;
  meldedatumEnd: string;
  schadenStatus: string;
  checklisteStatus: string;
  schadendatum: string;
  schadendatumStart: string;
  schadendatumEnd: string;
  vertragssparte: string;
  kundenName: string;
  benutzer: string;
  betreuerNummer: string;
  stati = [
    'angelegt',
    'gemeldet',
    'erledigt',
    'storniert',
    'abgelehnt'
  ];
  checklisteStati = [
    'P',
    'T',
    'N',
    'K'
  ];
  filterValues = new FilterValues();
  inputCtrl = new UntypedFormControl();
  currentChips: OverviewFilterChip[] = [];
  separatorKeysCodes = [
    ENTER,
    COMMA
  ];
  schadendatumRangeGroup = new UntypedFormGroup({
    start: new UntypedFormControl(),
    end:   new UntypedFormControl()
  });
  meldedatumRangeGroup = new UntypedFormGroup({
    start: new UntypedFormControl(),
    end:   new UntypedFormControl()
  });

  private filterPolizzenNr: string;
  private filterSchadenNr: string;

  @Input()
  isSchadenTab: boolean;

  @Output()
  filter = new EventEmitter<FilterValues>();

  @Output()
  globalFilterEmitter = new EventEmitter<string>();

  @Input()
  isARGE: boolean;

  @Input()
  isReset: boolean;

  @Input()
  isKbma: boolean;

  @ViewChild('filterInput') filterInput: ElementRef<HTMLInputElement>;
  @ViewChild('menuTrigger') filterMenuTrigger: MatMenuTrigger;

  ngOnInit() {
    this.inputCtrl.valueChanges
      .pipe(untilDestroyed(this), debounceTime(300), distinctUntilChanged())
      .subscribe(inputCtrlValue => this.globalFilterEmitter.emit(inputCtrlValue));
  }

  ngOnChanges(): void {
    if (this.isReset) {
      this.inputCtrl.setValue('');
      this.isReset = false;
      this.currentChips = [];
      this.filterValues = new FilterValues();
      this.applyFilter();
    }
  }

  applyFilter(): void {
    this.filterMenuTrigger.closeMenu();
    if (this.schadenNr) {
      this.filterSchadenNr = checkForSpecialCharacters(this.schadenNr) ? replaceSpecialCharacters(this.schadenNr) : this.schadenNr;
      this.removeChipByType(Chip_Type.SCHADEN_NR);
      this.currentChips.push({
        label:      'Schadennummer: ' + this.schadenNr,
        chipType:   Chip_Type.SCHADEN_NR,
        filtertext: this.schadenNr
      });
      this.filterValues.schadenNummer = this.filterSchadenNr;
    } else {
      this.filterValues.schadenNummer = '';
    }
    if (this.schadendatumStart && this.schadendatumEnd) {
      this.removeChipByType(Chip_Type.SCHADEN_DATUM_RANGE);
      this.currentChips.push({
        label:
          'Schadendatum von: ' +
          moment(this.schadendatumStart)
            .format('DD.MM.YYYY') +
          ' bis: ' +
          moment(this.schadendatumEnd)
            .format('DD.MM.YYYY'),
        chipType:             Chip_Type.SCHADEN_DATUM_RANGE,
        filtertext:           this.schadendatumEnd,
        filterDateRangeStart: this.schadendatumStart,
        filterDateRangeEnd:   this.schadendatumEnd
      });
      this.filterValues.schadenDatumStart = moment(this.schadendatumStart)
        .format('YYYY-MM-DD');
      this.filterValues.schadenDatumEnd = moment(this.schadendatumEnd)
        .format('YYYY-MM-DD');
    } else {
      this.filterValues.schadenDatumStart = '';
      this.filterValues.schadenDatumEnd = '';
    }
    if (this.meldedatumStart && this.meldedatumEnd) {
      this.removeChipByType(Chip_Type.MELDE_DATUM_RANGE);
      this.currentChips.push({
        label:
          'Meldedatum von: ' +
          moment(this.meldedatumStart)
            .format('DD.MM.YYYY') +
          ' bis: ' +
          moment(this.meldedatumEnd)
            .format('DD.MM.YYYY'),
        chipType:             Chip_Type.MELDE_DATUM_RANGE,
        filtertext:           this.meldedatumEnd,
        filterDateRangeStart: this.meldedatumStart,
        filterDateRangeEnd:   this.meldedatumEnd
      });
      this.filterValues.meldeDatumStart = moment(this.meldedatumStart)
        .format('YYYY-MM-DD');
      this.filterValues.meldeDatumEnd = moment(this.meldedatumEnd)
        .format('YYYY-MM-DD');
    } else {
      this.filterValues.meldeDatumStart = '';
      this.filterValues.meldeDatumEnd = '';
    }
    if (this.vertragssparte) {
      this.removeChipByType(Chip_Type.VERTRAGS_SPARTE);
      this.currentChips.push({
        label:      'Vertragssparte: ' + this.vertragssparte,
        chipType:   Chip_Type.VERTRAGS_SPARTE,
        filtertext: this.vertragssparte
      });
      this.filterValues.vertragsSparte = this.vertragssparte;
    } else {
      this.filterValues.vertragsSparte = '';
    }
    if (this.kundenName) {
      this.removeChipByType(Chip_Type.KUNDEN_NAME);
      this.currentChips.push({
        label:      'Kundenname: ' + this.kundenName,
        chipType:   Chip_Type.KUNDEN_NAME,
        filtertext: this.kundenName
      });
      this.filterValues.kundeNachname = this.kundenName;
    } else {
      this.filterValues.kundeNachname = '';
    }
    if (this.schadenStatus) {
      this.removeChipByType(Chip_Type.SCHADEN_STATUS);
      this.currentChips.push({
        label:      'Status: ' + this.schadenStatus,
        chipType:   Chip_Type.SCHADEN_STATUS,
        filtertext: this.schadenStatus
      });
      this.filterValues.schadenStatus = this.schadenStatus;
    } else {
      this.filterValues.schadenStatus = '';
    }
    if (this.checklisteStatus) {
      this.removeChipByType(Chip_Type.CHECKLISTE_STATUS);
      this.currentChips.push({
        label:      'Checkliste: ' + this.convertToCheckListStatusText(this.checklisteStatus),
        chipType:   Chip_Type.CHECKLISTE_STATUS,
        filtertext: this.convertToCheckListStatusText(this.checklisteStatus)
      });
      this.filterValues.checkListStatus = this.checklisteStatus;
    } else {
      this.filterValues.checkListStatus = '';
    }
    if (this.betreuerNummer) {
      this.removeChipByType(Chip_Type.BETREUER_NUMMER);
      this.currentChips.push({
        label:      'Betreuernummer: ' + this.betreuerNummer,
        chipType:   Chip_Type.BETREUER_NUMMER,
        filtertext: this.betreuerNummer
      });
      this.filterValues.betreuerNummer = this.betreuerNummer;
    } else {
      this.filterValues.betreuerNummer = '';
    }
    // the filter by benutzer looks for the surname of the last bearbeiter
    if (this.benutzer) {
      this.removeChipByType(Chip_Type.BENUTZER);
      this.currentChips.push({
        label:      'Benutzer: ' + this.benutzer,
        chipType:   Chip_Type.BENUTZER,
        filtertext: this.benutzer
      });
      this.filterValues.letzterBearbeiterNachname = this.benutzer;
    } else {
      this.filterValues.letzterBearbeiterNachname = '';
    }
    if (!this.isSchadenTab) {
      if (this.polizzenNr) {
        this.filterPolizzenNr = checkForSpecialCharacters(this.polizzenNr) ? replaceSpecialCharacters(this.polizzenNr) : this.polizzenNr;
        this.removeChipByType(Chip_Type.POLIZZEN_NR);
        this.currentChips.push({
          label:      'Polizzennummer: ' + this.polizzenNr,
          chipType:   Chip_Type.POLIZZEN_NR,
          filtertext: this.polizzenNr
        });
        this.filterValues.polizzeNummer = this.filterPolizzenNr;
      } else {
        this.filterValues.polizzeNummer = '';
      }
    }
    this.clearMenu();
    this.filter.emit(this.filterValues);
  }

  convertToCheckListStatusText(checkListStatus) {
    switch (checkListStatus) {
      case 'K':
        return 'Kein GSM Schaden';
      case 'P':
        return 'Prüfbar/Vollständig';
      case 'N':
        return 'Nicht prüfbar/Unvollständig';
      case 'T':
        return 'Teilweise prüfbar/Unvollständig';
      default:
        return 'Unbekannt';
    }
  }

  clearMenu(): void {
    this.schadendatumStart = null;
    this.schadendatumEnd = null;
    this.meldedatumStart = null;
    this.meldedatumEnd = null;
    this.vertragssparte = null;
    this.kundenName = null;
    this.benutzer = null;
    this.betreuerNummer = null;
    this.schadenNr = null;
    this.meldedatum = null;
    this.schadenStatus = null;
    this.checklisteStatus = null;
    this.polizzenNr = null;
    this.filterPolizzenNr = null;
    this.filterSchadenNr = null;
  }

  removeChip(category: OverviewFilterChip): void {
    this.filterInput.nativeElement.focus();
    const index = this.currentChips.findIndex(c => c.filtertext === category.filtertext && c.label === category.label);
    this.currentChips.splice(index, 1);
    this.fillMenu(false);
    this.applyFilter();
  }

  removeChipByType(chipType: Chip_Type) {
    if (this.currentChips.find(x => x.chipType === chipType)) {
      const duplicateElmIndex = this.currentChips.findIndex(x => x.chipType === chipType);
      if (duplicateElmIndex >= 0) {
        this.currentChips.splice(duplicateElmIndex, 1);
      }
    }
  }

  fillMenu(openMenu: boolean): void {
    this.clearMenu();

    type localChipProps = keyof Pick<this, 'schadenNr' | 'polizzenNr' | 'vertragssparte' | 'kundenName' | 'schadenStatus' | 'checklisteStatus' | 'betreuerNummer' | 'benutzer'>;
    const chipPropertyMap = new Map<Chip_Type, localChipProps>([
      [
        Chip_Type.SCHADEN_NR,
        'schadenNr'
      ],
      [
        Chip_Type.POLIZZEN_NR,
        'polizzenNr'
      ],
      [
        Chip_Type.VERTRAGS_SPARTE,
        'vertragssparte'
      ],
      [
        Chip_Type.KUNDEN_NAME,
        'kundenName'
      ],
      [
        Chip_Type.SCHADEN_STATUS,
        'schadenStatus'
      ],
      [
        Chip_Type.CHECKLISTE_STATUS,
        'checklisteStatus'
      ],
      [
        Chip_Type.BETREUER_NUMMER,
        'betreuerNummer'
      ],
      [
        Chip_Type.BENUTZER,
        'benutzer'
      ]
    ]);

    this.currentChips.forEach(chip => {
      const localProp = chipPropertyMap.get(chip.chipType);
      if (!!localProp) {
        this[localProp] = chip.filtertext;
      }

      if (chip.chipType === Chip_Type.SCHADEN_DATUM_RANGE) {
        this.schadendatumStart = chip.filterDateRangeStart;
        this.schadendatumEnd = chip.filterDateRangeEnd;
      } else {
        this.schadendatumStart = this.schadendatumEnd = null;
      }
      if (chip.chipType === Chip_Type.MELDE_DATUM_RANGE) {
        this.meldedatumStart = chip.filterDateRangeStart;
        this.meldedatumEnd = chip.filterDateRangeEnd;
      } else {
        this.meldedatumStart = this.meldedatumEnd = null;
      }
    });
    if (openMenu) {
      this.filterMenuTrigger.openMenu();
    }
  }
}

export interface OverviewFilterChip {
  label: string;
  chipType?: Chip_Type;
  filtertext: string;
  filterDateRangeStart?: string;
  filterDateRangeEnd?: string;
}

export enum Chip_Type {
  SCHADEN_NR = 'schadennummer',
  POLIZZEN_NR = 'polizzennummer',
  SCHADEN_DATUM_RANGE = 'schadendatumRange',
  MELDE_DATUM_RANGE = 'meldedatumRange',
  VERTRAGS_SPARTE = 'vertragssparte',
  KUNDEN_NAME = 'kundenname',
  SCHADEN_STATUS = 'schadenstatus',
  BETREUER_NUMMER = 'betreuernummer',
  BENUTZER = 'benutzer',
  CHECKLISTE_STATUS = 'checklistestatus',
}

export class FilterValues implements SchadenUebersicht {
  betreuerNummer = '';
  betreuerName = '';
  checkListStatus = '';
  checklistItemList: ChecklistItem[];
  gesamtZahlung = 0;
  kundeId = '';
  kundeNachname = '';
  kundeVorname = '';
  letzteBearbeitungDatum = '';
  letzterBearbeiterNachname = '';
  letzterBearbeiterVorname = '';
  meldeDatum = '';
  meldeDatumStart = '';
  meldeDatumEnd = '';
  polizzeNummer = '';
  processInstanceId = '';
  referentNachname = '';
  referentVorname = '';
  requiredProcess: boolean;
  schadenDatum = '';
  schadenDatumStart = '';
  schadenDatumEnd = '';
  schadenNummer = '';
  schadenStatus = '';
  schadenVariante: number;
  vertragsSparte = '';
  referent = '';
  kundenbueroMitarbeiter: boolean;
}
